import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Badge, Table } from "react-bootstrap";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { MdLocalLaundryService } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = {
  name: "",
  capacity_kg: "",
  notes: "",
  sort_order: "",
  is_active: true,
};

const WashingMachines = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/washing-machines");
      setMachines(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Gagal memuat data mesin cuci");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditId(null);
    setForm({
      ...emptyForm,
      sort_order: String(
        machines.reduce((max, m) => Math.max(max, Number(m.sort_order) || 0), 0) +
          1,
      ),
    });
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name,
      capacity_kg: m.capacity_kg ?? "",
      notes: m.notes ?? "",
      sort_order: m.sort_order ?? "",
      is_active: Boolean(m.is_active),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nomor mesin wajib diisi");
      return;
    }
    if (
      form.capacity_kg !== "" &&
      (isNaN(Number(form.capacity_kg)) || Number(form.capacity_kg) <= 0)
    ) {
      toast.error("Kapasitas harus angka lebih dari 0");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        capacity_kg: form.capacity_kg === "" ? null : Number(form.capacity_kg),
        notes: form.notes.trim(),
        sort_order: form.sort_order === "" ? 0 : Number(form.sort_order),
        is_active: form.is_active,
      };
      if (editId) {
        await api.put(`/washing-machines/${editId}`, payload);
        toast.success("Mesin cuci diperbarui");
      } else {
        await api.post("/washing-machines", payload);
        toast.success("Mesin cuci ditambahkan");
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/washing-machines/${deleteTarget.id}`);
      toast.success("Mesin cuci dihapus");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menghapus");
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Mesin Cuci</h4>
          <small className="text-muted">
            Mesin yang aktif akan muncul saat menandai transaksi sebagai
            "Dicuci". Satu transaksi bisa memakai lebih dari satu mesin.
          </small>
        </div>
        <Button variant="primary" onClick={openAdd}>
          <FiPlus className="me-1" /> Tambah
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="card shadow-sm">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Urutan</th>
                <th>Nomor Mesin</th>
                <th>Kapasitas</th>
                <th>Keterangan</th>
                <th>Dipakai</th>
                <th>Status</th>
                <th className="text-end">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {machines.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    Belum ada mesin cuci
                  </td>
                </tr>
              ) : (
                machines.map((m) => (
                  <tr key={m.id}>
                    <td className="text-muted">{m.sort_order}</td>
                    <td className="fw-semibold">
                      <MdLocalLaundryService
                        className="me-1 text-primary"
                        size={18}
                      />
                      {m.name}
                    </td>
                    <td>
                      {m.capacity_kg != null ? (
                        `${Number(m.capacity_kg)} kg`
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {m.notes || <span className="text-muted">-</span>}
                    </td>
                    <td>
                      <span className="text-muted small">
                        {m.usage_count || 0} transaksi
                      </span>
                    </td>
                    <td>
                      {m.is_active ? (
                        <Badge bg="success">Aktif</Badge>
                      ) : (
                        <Badge bg="secondary">Nonaktif</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => openEdit(m)}
                      >
                        <FiEdit2 />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setDeleteTarget(m)}
                      >
                        <FiTrash2 />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit" : "Tambah"} Mesin Cuci</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Nomor Mesin</Form.Label>
            <Form.Control
              type="text"
              placeholder="Contoh: Mesin 1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={50}
              autoFocus
            />
          </Form.Group>
          <div className="row">
            <div className="col-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Kapasitas (kg)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="opsional"
                  value={form.capacity_kg}
                  onChange={(e) =>
                    setForm({ ...form, capacity_kg: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Urutan Tampil</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm({ ...form, sort_order: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Keterangan</Form.Label>
            <Form.Control
              type="text"
              placeholder="opsional, contoh: khusus bed cover"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={255}
            />
          </Form.Group>
          <Form.Group>
            <Form.Check
              type="switch"
              label="Aktif (bisa dipilih saat proses cuci)"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Hapus Mesin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Yakin ingin menghapus <strong>{deleteTarget?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteTarget(null)}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Hapus
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WashingMachines;
